import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STATUS_CONFIG = {
  PROVEN: {
    label: 'PROVEN',
    pill: 'bg-teal-50 border-teal-200 text-teal-700',
    icon: '●',
    description: 'Directly anchored to verified, immutable source evidence. Hash chain intact.',
  },
  INFERRED: {
    label: 'INFERRED',
    pill: 'bg-amber-50 border-amber-200 text-amber-700',
    icon: '◎',
    description: 'Logically derived from multiple evidence records. Not directly observed in a single source.',
  },
  UNVERIFIABLE: {
    label: 'UNVERIFIABLE',
    pill: 'bg-red-50 border-red-200 text-red-700',
    icon: '○',
    description: 'Source data missing, retention gap, or broken lineage. Cannot be proven.',
  },
  STALE: {
    label: 'STALE',
    pill: 'bg-slate-100 border-slate-300 text-slate-600',
    icon: '◌',
    description: 'Evidence exists but has exceeded its revalidation interval. Proof quality degraded.',
  },
  CONTRADICTORY: {
    label: 'CONTRADICTORY',
    pill: 'bg-purple-50 border-purple-200 text-purple-700',
    icon: '⊘',
    description: 'Multiple authoritative sources disagree. Reconciliation required before proof can be established.',
  },
  DECAYED: {
    label: 'DECAYED',
    pill: 'bg-orange-50 border-orange-200 text-orange-700',
    icon: '↓',
    description: 'Confidence has degraded due to loss of corroborating sources or unresolved contradictions.',
  },
};

const REASON_LABELS = {
  MISSING_CLOUDTRAIL_DATA_EVENT: 'CloudTrail data events not enabled',
  CONFIG_GAP: 'AWS Config delivery interrupted',
  LATE_ARRIVAL: 'Event arrived after processing window',
  CROSS_SYSTEM_MISMATCH: 'Source systems disagree',
  APPROVAL_RECORD_UNAVAILABLE: 'Change ticket not found',
  RETENTION_EXPIRED: 'CloudTrail retention window exceeded',
  HASH_MISMATCH: 'Source partition hash failed verification',
};

export default function ProofStatusBadge({
  status = 'PROVEN',
  reasonCode = null,
  size = 'sm',
  showLabel = true,
  testId,
  displayOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.PROVEN;
  const textColor = cfg.pill.split(' ').find((c) => c.startsWith('text-'));

  // displayOnly mode — inert pill suitable for embedding inside a clickable parent row.
  // Renders a non-interactive <span> with a native title tooltip so it neither swallows the
  // parent click nor introduces nested-interactive accessibility violations. Pointer events stay
  // on the badge so the parent can detect "click originated on the badge" via e.target.closest().
  if (displayOnly) {
    const titleParts = [`${cfg.label} — ${cfg.description}`];
    if (reasonCode && REASON_LABELS[reasonCode]) titleParts.push(`Reason: ${REASON_LABELS[reasonCode]}`);
    return (
      <span
        data-testid={testId ?? `proof-badge-${status.toLowerCase()}`}
        title={titleParts.join('  ·  ')}
        aria-label={titleParts.join(' — ')}
        className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono select-none ${
          size === 'xs' ? 'text-[9px]' : 'text-[10px]'
        } ${cfg.pill}`}
      >
        <span>{cfg.icon}</span>
        {showLabel && <span className="font-semibold tracking-wider">{cfg.label}</span>}
      </span>
    );
  }

  return (
    <span className="relative inline-block" data-testid={testId ?? `proof-badge-${status.toLowerCase()}`}>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); } }}
        className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono cursor-pointer transition-opacity hover:opacity-80 select-none ${
          size === 'xs' ? 'text-[9px]' : 'text-[10px]'
        } ${cfg.pill}`}
      >
        <span>{cfg.icon}</span>
        {showLabel && <span className="font-semibold tracking-wider">{cfg.label}</span>}
      </span>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 bottom-full mb-1.5 left-0 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
            data-testid={`${testId ?? `proof-badge-${status.toLowerCase()}`}-tooltip`}
          >
            <div className={`text-[10px] font-bold font-mono mb-1 ${textColor}`}>
              {cfg.icon} {cfg.label}
            </div>
            <p className="text-[11px] text-apex-neutral leading-relaxed">{cfg.description}</p>
            {reasonCode && REASON_LABELS[reasonCode] && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <span className="text-[10px] font-mono text-red-600">
                  Reason: {REASON_LABELS[reasonCode]}
                </span>
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-slate-100 text-[9px] font-mono text-slate-400">
              Click to dismiss
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}
